import { useEffect } from "react";
import Button from "components/atoms/Button";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import TableLoadingWrapper from "components/templates/TableLoadingWrapper";
import { StyledTableCell, StyledTableRow } from "components/templates/Tables";
import {
    Table,
    TableRow,
    TableBody,
    TableHead,
    TableContainer,
    Typography,
    Box,
} from "@mui/material";
import { MODAL, modalActions } from "redux/slices/modal";
import DynamicService from "services/dynamics.service";
import BlockComponent from "components/organisms/BlockComponent/BlockComponent";
import { LookupConfig } from "./lookups";

function capitalizeFirstLetter(str: any) {
    if (!str) return "";
    return str[0].toUpperCase() + str.slice(1);
}

/**
 * Replaces the old DynamicCarMovement + DynamicSelections pair, which were the
 * same table with different wiring. `lookup.source` selects the service, slice
 * and modal; everything else is shared.
 */
export default function DynamicLookupList({ lookup }: { lookup: LookupConfig }) {
    const { type, label, plural, source } = lookup;
    const dispatch = useAppDispatch();

    const isCarMovement = source === "carMovement";
    const items = useAppSelector((state) =>
        isCarMovement
            ? state.dynamics.dynamicCarMovements
            : state.dynamics.dynamicSelections
    );
    const loading = useAppSelector((state) => state.dynamics.loading);
    const modalType = isCarMovement
        ? MODAL.DYNAMIC_CAR_MOVEMENT
        : MODAL.DYNAMIC_SELECTIONS;

    useEffect(() => {
        if (isCarMovement) DynamicService.getDynamicCarMovement(type);
        else DynamicService.getDynamicSection(type);
    }, [type, isCarMovement]);

    return (
        <>
            <Box sx={{
                mb: 2,
                gap: 2,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between"
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
                <Button variant="contained"
                    onClick={() =>
                        dispatch(
                            modalActions.openModal({
                                type: modalType,
                                data: { type },
                                width: "500px",
                            })
                        )
                    }
                >
                    {`Add ${label}`}
                </Button>
            </Box>
            <TableContainer>
                <Table
                    aria-label="customized table"
                    sx={{
                        minWidth: "100%",
                        borderSpacing: "0 10px",
                        borderBottomWidth: "0px",
                        borderCollapse: "separate",
                    }}>
                    <TableLoadingWrapper
                        coloumns={4}
                        loading={loading}
                        length={items?.length}
                        message={`No ${plural} are available`}>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <BlockComponent permission={"dynamics:edit"}>
                                    <StyledTableCell align="right">Action</StyledTableCell>
                                </BlockComponent>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item: any, i: number) => (
                                <StyledTableRow key={i + 1}>
                                    <StyledTableCell>
                                        {capitalizeFirstLetter(item?.name)}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {item.is_active ? "Active" : "In-active"}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <BlockComponent permission={"dynamics:edit"}>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() =>
                                                    dispatch(
                                                        modalActions.openModal({
                                                            type: modalType,
                                                            data: {
                                                                id: item?._id,
                                                                info: item,
                                                                type,
                                                            },
                                                            width: "500px",
                                                        })
                                                    )
                                                }>
                                                Edit
                                            </Button>
                                        </BlockComponent>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </TableLoadingWrapper>
                </Table>
            </TableContainer>
        </>
    );
}
